import { DeleteConfirmationModal } from "@mirage/mosaics/ComposeAssistant/components/settings/DeleteConfirmationModal";
import { useConversationsList } from "@mirage/service-conversation/hooks/useConversationsList";
import { isAutoGeneratedConversation } from "@mirage/service-conversation/utils/typeguards";
import i18n from "@mirage/translations";
import { useAtom } from "jotai";
import {
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ConversationListPopup } from "../components/sessions/ConversationListPopup";
import { ConversationListRows } from "../components/sessions/ConversationListRows";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import { PAP_Click_PreviousSession } from "@mirage/analytics/events/types/click_previous_session";
import {
  parseConversationSearchParams,
  urlForConversationWithServerID,
} from "@mirage/mosaics/Chat/utils/navigation";
import { useComposeSessionsContext } from "@mirage/mosaics/ComposeAssistant/data/ComposeSessionsContext";
import { conversationPageLoadingAtom } from "@mirage/mosaics/ConversationAssistant/hooks/useConversationPageLoading";
import { deleteConversations } from "@mirage/service-conversation";
import {
  CtaSource,
  type ConversationRouterState,
  type MessageHistory,
} from "@mirage/service-conversation/router_state_types";
import type {
  ConversationGroup,
  ConversationMetadata,
  UserMessageOptions,
} from "@mirage/service-conversation/types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import {
  DashSearch,
  SearchParams,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/context_engine_conversations_service_pb";
import type { ComposeAssistantConversationMessage } from "@mirage/shared/compose/assist-message";
import {
  getFirstMarkdownArtifact,
  type ComposeSession,
} from "@mirage/shared/compose/compose-session";
import { cleanTitle } from "@mirage/shared/search/recent-chats";
import { useWindowSize } from "@react-hookz/web";
import { ConversationListModal } from "../components/sessions/ConversationListModal";
import { useChatRoute } from "../hooks/useChatRoute";

const DISPLAY_CONFIG = {
  ROWS: {
    MAX_ITEMS: 5,
  },
  POPUP: {
    MAX_ITEMS: 7,
  },
} as const;

const EMPTY_ARRAY: never[] = [];

// Transform legacy session message to conversation message format
const transformMessageToHistory = (
  message: ComposeAssistantConversationMessage,
): MessageHistory => ({
  role:
    message.role === "user" || message.role === "assistant"
      ? message.role
      : "assistant", // Default to assistant for system or other roles
  content: message.type === "message" ? message.text : "",
  attachments:
    message.type === "message" && message.referencingSources
      ? message.referencingSources
      : [],
});

interface ConversationListProps {
  showListPreview: boolean;
  onOpenTemplates: () => void;
  handleStartNewSessionWithState: (
    routerState?: ConversationRouterState,
  ) => void;
  logComposeEvent: (event: PAPEvent) => void;
  clearEmptyChatOption: () => void;
  setUserMessageOptions: (options: SetStateAction<UserMessageOptions>) => void;
}

export const ConversationList = memo(
  ({
    showListPreview,
    onOpenTemplates,
    handleStartNewSessionWithState,
    logComposeEvent,
    clearEmptyChatOption,
    setUserMessageOptions,
  }: ConversationListProps) => {
    const navigate = useNavigate();
    const chatRoute = useChatRoute();

    const windowSize = useWindowSize();
    const isTooNarrow = windowSize.width < 1170;
    const isTooShortAndNarrow =
      windowSize.height < 850 && windowSize.width < 1000;
    const showList = showListPreview && !isTooNarrow && !isTooShortAndNarrow;

    const [showModal, _setShowModal] = useState(false);
    const [conversationToDelete, setConversationToDelete] =
      useState<ConversationMetadata | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const { serverConversationId } =
      parseConversationSearchParams(searchParams);
    const conversationListState = useConversationsList();
    const [, setIsConversationPageLoading] = useAtom(
      conversationPageLoadingAtom,
    );
    const { sessions: legacySessions = [] } = useComposeSessionsContext();

    const setShowModal = useCallback(
      (show: boolean) => {
        // Remove query parameter if modal is closed by the user
        const searchParams = new URLSearchParams(window.location.search);
        if (!show && searchParams.get("showHistory") !== null) {
          searchParams.delete("showHistory");
          setSearchParams(searchParams);
        }
        _setShowModal(show);
      },
      [setSearchParams],
    );

    useEffect(() => {
      if (searchParams.get("showHistory") !== null) {
        setShowModal(true);
      }
    }, [searchParams, setSearchParams, setShowModal]);

    // Sort legacy sessions in reverse chronological order
    const sortedLegacySessions = useMemo(
      () =>
        [...legacySessions].sort(
          (a, b) => (b.lastUpdated ?? 0) - (a.lastUpdated ?? 0),
        ),
      [legacySessions],
    );

    const handleLegacySessionClick = useCallback(
      (session: ComposeSession, onClose?: () => void) => {
        const messageHistory = session.messagesHistory.map(
          transformMessageToHistory,
        );
        const markdownArtifact = getFirstMarkdownArtifact(session.artifacts);
        const conversationRouterState = buildConversationRouterState(
          CtaSource.LegacySession,
          {
            messageHistory,
            markdownContent: markdownArtifact?.markdownContent,
          },
        );
        clearEmptyChatOption();
        handleStartNewSessionWithState(conversationRouterState);
        onClose?.();
      },
      [handleStartNewSessionWithState, clearEmptyChatOption],
    );

    const handleClickConversation = useCallback(
      (conversation: ConversationMetadata) => {
        setIsConversationPageLoading(true);
        clearEmptyChatOption();
        // Reset the user message options when opening a past conversation to avoid bleed over from the previous session.
        // There's probably a smarter way to do this based on the conversation metadata/client context (e.g. disabling search
        // by default for Stack Chats), but this approach is better than doing no reset.
        setUserMessageOptions({
          searchParams: new SearchParams({
            searchTarget: {
              value: new DashSearch(),
              case: "dashSearch",
            },
          }),
        });
        navigate(
          urlForConversationWithServerID(
            conversation.serverConversationId,
            chatRoute,
          ),
          {
            replace: true,
          },
        );
        logComposeEvent(
          PAP_Click_PreviousSession({
            dashActionSurface: "conversation_page",
            actionSurfaceComponent: "chat_history",
            featureLine: "assist",
          }),
        );
      },
      [navigate, setIsConversationPageLoading, chatRoute, clearEmptyChatOption],
    );

    const handleDeleteConversation = useCallback(
      (conversation: ConversationMetadata) => {
        setConversationToDelete(conversation);
      },
      [],
    );

    const handleConfirmDelete = useCallback(() => {
      if (conversationToDelete) {
        deleteConversations([conversationToDelete.serverConversationId]);
        setConversationToDelete(null);
      }
    }, [conversationToDelete]);

    const handleCancelDelete = useCallback(() => {
      setConversationToDelete(null);
    }, []);

    const conversationList =
      (conversationListState.type === "loaded" &&
        conversationListState.conversationMetadatas.length > 0 &&
        conversationListState.conversationMetadatas
          // Note: we're filtering out conversations that are auto-generated (created by default on SERP)
          // but have not had any follow-up / user interactions
          // Longer-term: we should not persist this on the SERP by default
          // https://dropbox.atlassian.net/browse/OTCA-862
          .filter((m) => !isAutoGeneratedConversation(m))
          .map((m) => ({
            ...m,
            title: cleanTitle(m.title),
            preview: cleanTitle(m.preview),
          }))) ||
      EMPTY_ARRAY;

    const getConversationsToDisplay = useCallback(
      (maxItems: number) => {
        const result: (ConversationMetadata | ComposeSession)[] =
          conversationList.slice(0, maxItems);

        const remainingSlots = maxItems - result.length;
        if (remainingSlots > 0) {
          result.push(...sortedLegacySessions.slice(0, remainingSlots));
        }
        return result;
      },
      [conversationList, sortedLegacySessions],
    );

    const conversationsToDisplayInRows = useMemo(
      () => getConversationsToDisplay(DISPLAY_CONFIG.ROWS.MAX_ITEMS),
      [getConversationsToDisplay],
    );

    const conversationsToDisplayInPopup = useMemo(() => {
      return conversationList.slice(0, DISPLAY_CONFIG.POPUP.MAX_ITEMS);
    }, [conversationList]);

    const legacyConversationToDisplayInPopup = useMemo(() => {
      const remainingSlots =
        DISPLAY_CONFIG.POPUP.MAX_ITEMS - conversationsToDisplayInPopup.length;
      if (remainingSlots > 0) {
        return sortedLegacySessions.slice(0, remainingSlots);
      }
      return [];
    }, [conversationsToDisplayInPopup.length, sortedLegacySessions]);

    const groupedConversations = (conversations: ConversationMetadata[]) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const groups: ConversationGroup[] = [
        {
          title: i18n.t("assist_conversation_list_modal_title_today"),
          conversations: [],
        },
        {
          title: i18n.t("assist_conversation_list_modal_title_yesterday"),
          conversations: [],
        },
        {
          title: i18n.t(
            "assist_conversation_list_modal_title_previous_30_days",
          ),
          conversations: [],
        },
      ];
      conversations.forEach((conversation) => {
        const timestamp = new Date(conversation.created);
        if (timestamp >= today) {
          groups[0].conversations.push(conversation);
        } else if (timestamp >= yesterday) {
          groups[1].conversations.push(conversation);
        } else if (timestamp >= thirtyDaysAgo) {
          groups[2].conversations.push(conversation);
        }
      });
      return groups.filter((group) => group.conversations.length > 0);
    };

    const groupedConversationsInPopup = useMemo(() => {
      return groupedConversations(conversationsToDisplayInPopup);
    }, [conversationsToDisplayInPopup]);

    const groupedConversationsInModal = useMemo(() => {
      return groupedConversations(conversationList);
    }, [conversationList]);

    return (
      <>
        <ConversationListPopup
          onClickConversation={handleClickConversation}
          setShowModal={setShowModal}
          disabled={showList}
          onDeleteConversation={handleDeleteConversation}
          onOpenTemplates={onOpenTemplates}
          handleLegacySessionClick={handleLegacySessionClick}
          groupedConversations={groupedConversationsInPopup}
          sortedLegacySessions={legacyConversationToDisplayInPopup}
          currentServerConversationId={serverConversationId}
        />
        {showList && (
          <ConversationListRows
            conversationListState={conversationListState}
            conversationsToDisplay={conversationsToDisplayInRows}
            currentServerConversationId={serverConversationId}
            onClickConversation={handleClickConversation}
            onDeleteConversation={handleDeleteConversation}
            setShowModal={setShowModal}
            handleLegacySessionClick={handleLegacySessionClick}
          />
        )}
        <ConversationListModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onClickConversation={handleClickConversation}
          onDeleteConversation={handleDeleteConversation}
          sortedLegacySessions={sortedLegacySessions}
          handleLegacySessionClick={handleLegacySessionClick}
          groupedConversations={groupedConversationsInModal}
          isLoading={conversationListState.type === "loading"}
          currentServerConversationId={serverConversationId}
        />
        {conversationToDelete && (
          <DeleteConfirmationModal
            title={i18n.t("compose_delete_session_confirm_title")}
            description={i18n.t("compose_delete_session_confirm_description")}
            onSubmit={handleConfirmDelete}
            onCancel={handleCancelDelete}
            isOpen={!!conversationToDelete}
          />
        )}
      </>
    );
  },
);

ConversationList.displayName = "ConversationList";
